import EncryptionContract from './contracts/EncryptionContract.js';
import EthereumTransaction from './transactions/EthereumTransaction.js';
import {ENCRYPTION} from './transactions/contracts.js';
import {ipfsHashToBytes32, bytes32ToIPFSHash} from 'utils/ipfsHash.js';
import accounts from 'accounts/Accounts.js';
import {SEQUENCE, PRESET} from 'constants/encrypted-content-types.js';
import BackendQuery from './BackendQuery.js';
import {NewEncryptedContent, NewPublicKey, EncryptedContentShared} from './EthereumEvents.js';

/**
 * Interfaces with the Encryption.sol smart contract, handling encrypted
 * sequences and presets
 */

class Encryption {
    constructor(web3, crypto) {
        this.contract = new EncryptionContract().getContract(web3);
        this.web3 = web3;
        this.crypto = crypto;
        this.NULL_BYTES32 = this.web3.utils.padLeft('0x0', 64);
        this.cachedResults = {};
    }

    async getPublicKey(address) {
        return new Promise(async (resolve, reject) => {
            // get the public key for this address;
            if (address === undefined) {
                address = await accounts.getAccount();
                if (address === undefined) {
                    resolve(undefined);
                    return;
                }
            }

            let query = new BackendQuery(this.contract.options.address)
                .from(NewPublicKey)
                .select("publicKey")
                .where('user', address.toLowerCase());

            let results = await query.executeQuery();
            if (results.length === 0) {
                resolve(undefined);
                return;
            }
            let publicKey = '0x' + this.stripTrailingZeroes(results[0].publicKey);
            resolve(this.web3.utils.toAscii(publicKey));
        });
    }

    async getEncryptedContent(hash, privateKey, address, fromBlock=2, toBlock) {
        return new Promise(async (resolve, reject) => {
            if (hash in this.cachedResults) {
                resolve(this.cachedResults[hash]);
                return;
            }
            let query = await new BackendQuery(this.contract.options.address)
                .from(NewEncryptedContent)
                .select('newContent')
                .select('user')
                .select('previousContent')
                .select('contentType')
                .select('encryptedName')
                .select('publicKey')
                .select('encryptedContentKey')
                .select('publicKey')
                .fromBlock(fromBlock)
                .toBlock(toBlock);

            if (address !== undefined) {
                query.where('user', address.toLowerCase());
            }

            if (hash !== undefined) {
                query.where('newContent', hash)
            }

            let dateA = new Date().getTime();
            let results = await query.executeQuery();
            let dateB = new Date().getTime();
            let time = dateB - dateA;
            if (results.length === 0) {
                resolve(undefined);
                return;
            }

            let converted = [];
            for (let i in results) {
                converted.push(await this.convertLogResult(results[i], privateKey));
            }
            let dateC = new Date().getTime();
            time = dateB - dateA;

            this.cachedResults[hash] = converted;

            resolve(converted);
        });
    }

    async getEncryptedContentSharedWithMe(hash, privateKey) {
        if (hash in this.cachedResults) {
            return this.cachedResults[hash];
        }

        const account = await accounts.getAccount();
        let query = new BackendQuery(this.contract.options.address)
            .from(EncryptedContentShared)
            .select('encryptedContentKey')
            .select('publicKey')
            .select('contentHash')
            .select('creator')
            .where('sharedWith', account.toLowerCase());

        if (hash !== undefined) {
            query.where('contentHash', hash);
        }

        let dateA = new Date().getTime();
        let results = await query.executeQuery();
        let dateB = new Date().getTime();
        let time = dateB - dateA;
        if (results.length === 0) {
            return undefined;
        }

        let converted = [];
        for (let i in results) {
            converted.push(await this.convertSharedLogResult(results[i], false, privateKey));
        }
        let dateC = new Date().getTime();
        time = dateC - dateA;

        
        this.cachedResults[hash] = converted;

        return converted;
    }


    async getEncryptedSequenceIfAny(hash, address, fromBlock=2, toBlock) {
        return await this.getEncryptedContent(hash, undefined, address, fromBlock, toBlock);
    }

    async getSequencesSharedWithUser(contentHash, fromBlock, toBlock) {
        const account = await accounts.getAccount();
        let query = new BackendQuery(this.contract.options.address)
            .from(EncryptedContentShared)
            .select('encryptedContentKey')
            .select('publicKey')
            .select('contentHash')
            .select('creator')
            .where('sharedWith', account.toLowerCase());

        if (contentHash !== undefined) {
            query.where('contentHash', contentHash);
        }

        if (fromBlock) {
            query.fromBlock(fromBlock);
        }
        if (toBlock) {
            query.toBlock(toBlock)
        }

        let results = await query.executeQuery();
        let converted = [];
        for (let i in results) {
            converted.push(await this.convertSharedLogResult(results[i]));
        }

        return converted;
    }

    async convertSharedLogResult(result, isSequence=true) {
        let contentKey = await this.crypto.decryptContentKey(result.encryptedContentKey);
        let publicKey = this.stripTrailingZeroes(result.publicKey);

        let ret = {
            user: result.creator,
            contentHash: result.contentHash,
            publicKey: publicKey,
            contentKey: contentKey ? contentKey.contentKey : undefined,
        };

        let sequence = {};
        let title = '';
        if (isSequence) {
            sequence = await this.getEncryptedSequenceIfAny(result.contentHash);
            title = await this.crypto.decryptName(
                sequence[0].encryptedTitle,
                this.stripTrailingZeroes(result.encryptedContentKey));
        }

        return {
            ... sequence[0],
            title: title,
            publicKey: publicKey,
            contentKey: contentKey ? contentKey.contentKey : undefined
        };
                
    }

    // converts the log result set for a NewEncryptedContent event into
    // a useable form.
    async convertLogResult(result, privateKey) {
        // todo: find out the length of the encrypted content key so we can slice it instead
        // of strip which will fail if the last valid character is 0
        let encryptedContentKey = this.stripTrailingZeroes(result.encryptedContentKey);
        let title = await this.crypto.decryptName(result.encryptedName, encryptedContentKey, privateKey)
        let user = result.user;
        let contentKey = await this.crypto.decryptContentKey(encryptedContentKey, privateKey);
        let ret = {
            title:  this.stripTrailingZeroes(title),
            encryptedTitle:  result.encryptedName,
            user: '0x' + this.stripLeadingZeroes(user.slice(2)),
            previousSequence: result.previousContent,
            newSequence: result.newContent,
            contentType: result.contentType,
            contentKey: contentKey ? contentKey.contentKey : undefined,
            blockNumber: result.blockNumber,
            publicKey: this.stripTrailingZeroes(result.publicKey),
        }

        return ret;
    }

    newPublicKey(masterPublicKey) {
        return new Promise(async (resolve, reject) => {
            new EthereumTransaction(
                ENCRYPTION,
                'setPublicKey',
                [masterPublicKey])
                .execute()
                .then(() => resolve());
        });
    }

    // the content key for this encrypted ipfs content is also encrypted
    // and we store the encrypted content key on Ethereum. The
    // content key can be decrypted using the user's master passphrase
    newEncryptedSequence(previousSequence, newSequence, encryptedName, encryptedContentKey, publicKey) {
        return new Promise(async (resolve, reject) => {
            if (previousSequence === undefined) {
                previousSequence = this.web3.utils.padLeft('0x0', 64);
            } else {
                previousSequence = ipfsHashToBytes32(previousSequence);
            }

            newSequence = ipfsHashToBytes32(newSequence);
            encryptedName = this.web3.utils.padLeft('0x' + encryptedName, 64);
            new EthereumTransaction(
                ENCRYPTION,
                'newEncryptedContent',
                [
                    previousSequence,
                    newSequence,
                    encryptedName,
                    SEQUENCE,
                    encryptedContentKey,
                    publicKey])
                .execute()
                .then(() => resolve()); 
        });
    }

    newEncryptedContent(
        contentHash,
        encryptedName,
        encryptedContentKey,
        contentType,
        publicKey) {
        return new Promise(async (resolve, reject) => {
            contentHash = ipfsHashToBytes32(contentHash);
            encryptedName = this.web3.utils.padLeft('0x' + encryptedName, 64);
            new EthereumTransaction(
                ENCRYPTION,
                'newEncryptedContent',
                [
                    this.NULL_BYTES32,
                    contentHash,
                    encryptedName,
                    contentType,
                    encryptedContentKey,
                    publicKey])
                .execute()
                .then(() => resolve());
        });
    }

    // share this sequence which via encrypting the content key using the
    // user's master public key
    shareEncryptedContent(
        shareWith,
        contentHash,
        encryptedContentKey,
        masterPublicKey)
    {
        return new Promise(async (resolve, reject) => {
            contentHash = ipfsHashToBytes32(contentHash);
            new EthereumTransaction(
                ENCRYPTION,
                'shareEncryptedContentWith',
                [
                    shareWith,
                    contentHash,
                    encryptedContentKey,
                    masterPublicKey])
                .execute()
                .then(() => resolve()); 
        });
    }


    stripTrailingZeroes(str) {
        for (let i=str.length - 1; i >= 0; i--) {
            if (str.slice(i, i + 1) !== '0') {
                return str.slice(0, i + 1);
            }
        }
    }

    stripLeadingZeroes(str) {
        for (let i=0; i < str.length; i++) {
            if (str.slice(i, i+1) !== '0') {
                return str.slice(i);
            }
        }
    }

    async getPotentialUsers() {
        let query = new BackendQuery(this.contract.options.address)
            .from(NewPublicKey)
            .select("user");
        
        let results = await query.executeQuery();
        let users = Array.from(new Set(results.map(x => x.user)));
        return users.filter(
            x => x !== undefined);
    }
}

export default Encryption;
